import React, { useRef, useState, useEffect } from "react";
import Papa from "papaparse";
import { validateInput } from "../utils";

const Uploader = ({ icon, acceptType, expectedHeaders, setData, disable = false, label, initialFileName, isEdit = false }) => {
  const inputRef = useRef();
  const isImage = acceptType === "image/*";
  const [error, setError] = useState("");
  const [selectedFileName, setSelectedFileName] = useState(initialFileName || "");
  const [isExistingFile, setIsExistingFile] = useState(initialFileName);

  // Update selectedFileName when initialFileName changes (for editing)
  useEffect(() => {
    if (initialFileName) {
      setSelectedFileName(initialFileName);
      setIsExistingFile(true);
    }
  }, [initialFileName]);

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = ""; // clear file input
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (result) => {
        const [headers, ...restData] = result.data;

        // Validate headers
        const isValid =
          expectedHeaders.length === headers?.length &&
          expectedHeaders.every(
            ({ label }, index) => label.trim() === headers[index]?.trim()
          );

        if (!isValid) {
          setError("CSV headers do not match the expected format. Please make sure your file matches the template.");
          setData(null, "");
          resetInput();
          return;
        }

        setError(""); // Clear any existing error
        let hasError = false;

        const csvData = restData.map((row, rowIndex) => {
          // Skip empty rows (not just lines)
          if (!row || row.every(cell => cell.trim() === "")) return null;

          const obj = {};

          for (let index = 0; index < expectedHeaders.length; index++) {
            const header = expectedHeaders[index];
            const cell = row[index] ?? "";

            if (header.type) {
              const error = validateInput(cell, header.type);
              if (error) {
                setError(`Row ${rowIndex + 2}: "${header.label}" is invalid — ${error}`);
                hasError = true;
                return null;
              }
            }

            obj[header.value] = cell;
          }

          return obj;
        }).filter(Boolean); // Remove any nulls

        if (hasError) {
          setData(null, "");
          resetInput();
          return;
        }

        if (!csvData.length) {
          setError("The CSV file contains no valid rows.");
          setData(null, "");
          resetInput();
          return;
        }

        setData(csvData, file?.name ?? "");
        setSelectedFileName(file?.name ?? "");
      },
      error: () => {
        setError("Failed to parse the CSV file. Please ensure it's a valid format.");
        setData(null, "");
        resetInput();
      },
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0] ?? null;

    // Check file size (limit to 1MB)
    if (file && file.size > 1 * 1024 * 1024) {
      setError("File size too large. Please select a file smaller than 5MB.");
      setData(null, "");
      resetInput();
      return;
    }

    setData(file, file?.name ?? "");
    setSelectedFileName(file ? file.name : "");
    setIsExistingFile(false);
    setError(file ? "" : "Unable to upload selected Image!!!");
  };

  const clearFile = () => {
    setSelectedFileName("");
    setData(null, "");
    setError("");
    setIsExistingFile(false);
    resetInput();
  };

  return (
    <div className="input-container">
      <label className="input-label">{label || `Upload ${isImage ? "Image" : "CSV"}`}</label>
      <div className="input-wrapper">
        <input
          ref={inputRef}
          className="input-box"
          type="file"
          accept={acceptType}
          disabled={disable}
          onChange={isImage ? handleImageUpload : handleFileUpload}
        />
        {icon && <span className="input-icon">{icon}</span>}
      </div>
      {error && <span className="error-message">{error}</span>}
      {(selectedFileName || isEdit) && (
        <div style={{ marginTop: '5px', fontSize: '12px', color: '#28a745', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span>{isEdit && !selectedFileName ? 'Uploaded' : (isExistingFile ? 'Uploaded' : `Selected: ${selectedFileName}`)}</span>
          <button
            type="button"
            onClick={clearFile}
            style={{
              background: 'none',
              border: 'none',
              color: '#dc3545',
              cursor: 'pointer',
              fontSize: '12px',
              textDecoration: 'underline'
            }}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default Uploader;